import React, { useState, useEffect, useCallback, useContext } from 'react';
import MessageModal from '../components/MessageModal';
import QuestionImportQuestionList from '../atoms/QuestionImportQuestionList';
import QuestionLogicRepresentation from '../molecules/QuestionLogicRepresentation';
import QAndA from '../organisms/QAndA';
import DaySelectionModal from '../atoms/DaySelectionModal';
import { CourseDesignContext } from '../context/CourseDesignContext';
import { StudentAnswerContext } from '../context/StudentAnswerContext';
import { generateUniqueId } from '../utils/courseDesignUtilities';

const SharedQuestionModule = ({ module, onBack }) => {
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [showMessage, setShowMessage] = useState(false);
  const [messageContent, setMessageContent] = useState({});
  const [isImporting, setIsImporting] = useState(false);
  const [viewMode, setViewMode] = useState('import'); // Add view mode state
  const [previewAnswers, setPreviewAnswers] = useState({}); // Track answers in student preview
  const [showDaySelectionModal, setShowDaySelectionModal] = useState(false); // Add modal state
  const [shouldNavigateBackOnClose, setShouldNavigateBackOnClose] = useState(false); // Track if success should navigate back

  const { courseDesignState, handleChange } = useContext(CourseDesignContext);

  // Auto-select all questions on load
  useEffect(() => {
    const allQuestionIds = module.questions.map(q => q.id);
    setSelectedQuestions(new Set(allQuestionIds));
  }, [module.questions]);

  // Handle question selection toggle
  const handleQuestionToggle = (questionId) => {
    const newSelected = new Set(selectedQuestions);
    if (newSelected.has(questionId)) {
      newSelected.delete(questionId);
    } else {
      newSelected.add(questionId);
    }
    setSelectedQuestions(newSelected);
  };

  // Select or deselect all questions
  const handleSelectAll = () => {
    const allQuestionIds = module.questions.map(q => q.id);
    setSelectedQuestions(new Set(allQuestionIds));
  };

  const handleDeselectAll = () => {
    setSelectedQuestions(new Set());
  };

    // Handler for the three-way view mode toggle
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  // Transform shared questions to courseDesignState format for QuestionLogicRepresentation
  const transformToCourseDesignState = () => {
    console.log('🔍 Transforming shared questions data:', module.questions);
    
    return module.questions.map((question, index) => {
      // Debug the question structure
      console.log(`Question ${index + 1}:`, {
        id: question.id,
        dataText: question.dataText,        // This is the correct field for question text
        questionText: question.questionText, // This doesn't exist in shared questions
        questionType: question.questionType,
        questionLogic: question.questionLogic
      });

      const transformed = {
        dataType: 'question',
        dataID: question.dataID || question.id.toString(), // Use original dataID if available, fallback to id
        dataText: question.dataText || question.questionText || `Shared Question ${question.id}`, // Use dataText first
        dataIndex: (index + 1).toString(),
        questionType: question.questionType || 'Unknown',
        questionOptions: question.questionOptions || '',
        questionLogic: question.questionLogic || '',
        questionCourseDate: 'shared-module', // Use a consistent date for shared modules
        // Add additional fields that might be expected
        sharerName: module.sharerName,
        questionModule: module.questionModule,
      };

      console.log(`Transformed question ${index + 1}:`, transformed);
      return transformed;
    });
  };

  // Transform shared questions for QAndA component (needs full courseDesignState structure)
  const transformForQAndA = () => {
    console.log('transformForQAndA called with module.questions:', module.questions);
    
    // Helper function to map shared question types to QAndA question types
    const mapQuestionType = (sharedType) => {
      console.log('Mapping question type:', sharedType);
      switch (sharedType) {
        case 'Radio':
          return 'Radio'; // Direct mapping - already correct type
        case 'MultipleChoice':
        case 'TrueFalse':
          return 'Radio'; // QAndA uses Radio for single-choice questions
        case 'Checkbox':
          return 'Checkbox'; // Direct mapping
        case 'LongAnswer':
          return 'LongAnswer'; // Direct mapping
        default:
          console.log('Unknown question type, defaulting to LongAnswer:', sharedType);
          return 'LongAnswer'; // Default fallback
      }
    };

    // Create a mock courseDesignState structure that QAndA expects
    const mockCourseDesignState = [
      // Create a mock section
      {
        dataType: 'sectionName',
        dataID: 'shared-section',
        dataText: 'Shared Questions',
        dataIndex: '1',
      },
      // Create a mock topic/day
      {
        dataType: 'topic',
        dataID: 'shared-topic',
        dataText: module.questionModule,
        dataIndex: '2',
        questionOptions: 'yes', // This is required for topics to show questions
      },
      // Transform all shared questions
      ...module.questions.map((question, index) => {
        const mappedType = mapQuestionType(question.questionType);
        console.log('Processing question:', question.id, 'Original Type:', `"${question.questionType}"`, 'Mapped Type:', `"${mappedType}"`, 'Logic:', question.questionLogic);
        return {
          dataType: 'question',
          dataID: question.dataID || question.id.toString(),
          dataText: question.dataText || `Shared Question ${question.id}`,
          dataIndex: (index + 3).toString(), // Start after section and topic
          questionType: mappedType,
          questionOptions: question.questionOptions || '',
          questionLogic: question.questionLogic || '', // Preserve original logic for conditional display
          questionCourseDate: 'shared-topic', // Link to our mock topic
        };
      })
    ];
    
    console.log('Mock courseDesignState for QAndA:', mockCourseDesignState);
    return mockCourseDesignState;
  };

  // Transform shared questions for QuestionImportQuestionList (expects questionText field)
  const transformForImportList = () => {
    return module.questions.map((question, index) => ({
      ...question,
      questionText: question.dataText || question.questionText || `Shared Question ${question.id}`, // Map dataText to questionText
      dataIndex: (index + 1).toString(), // Ensure we have a dataIndex
    }));
  };

  // Create a mock context value for QAndA
  const mockContextValue = {
    courseDesignState: transformForQAndA(),
    handleChange: () => {}, // No-op for read-only view
    removeFromDatabase: () => {}, // No-op for read-only view
    setCurrentThesis: () => {},
    currentThesis: null,
    thesisEditorIsOpen: false,
    hasContradictionItems: null,
    questionByQuestionGrading: null,
    giveStudentsComments: null,
    setThesisEditorIsOpen: () => {},
    prepareStateForUpsertToDatabase: () => {},
    updateDatabase: () => {},
    negativeUpdateDatabase: () => {},
  };

  // Create a functional StudentAnswerContext for interactive preview
  const handlePreviewAnswerChange = useCallback((username, questionID, answer) => {
    console.log('Answer changed:', { username, questionID, answer });
    setPreviewAnswers(prev => {
      const newAnswers = {
        ...prev,
        [username]: [
          ...(prev[username] || []).filter(item => item.questionID !== questionID),
          { questionID, questionAnswer: answer, username }
        ]
      };
      console.log('Updated preview answers:', newAnswers);
      return newAnswers;
    });
  }, []);

  const mockStudentAnswerContext = {
    studentAnswersState: { 'preview-user': previewAnswers['preview-user'] || [] },
    handleAnswerChange: handlePreviewAnswerChange,
    pendingChanges: [],
    setPendingChanges: () => {},
    updateDatabase: () => {},
    negativeUpdateDatabase: () => {},
  };

  // Handle import process - show day selection modal
  const handleImport = () => {
    if (selectedQuestions.size === 0) {
      setMessageContent({
        title: 'No Questions Selected',
        message: 'Please select at least one question to import.',
        type: 'error'
      });
      setShowMessage(true);
      return;
    }
    setShowDaySelectionModal(true);
  };

  // Handle day selection and import questions
  const handleDaySelection = async (selectedDayDataID) => {
    setShowDaySelectionModal(false);
    setIsImporting(true);

    try {
      // Get the selected questions data
      const questionsToImport = module.questions.filter(q => selectedQuestions.has(q.id));
      
      // Find the highest existing dataIndex for the selected day
      const existingQuestionsForDay = courseDesignState
        .filter(item => 
          item.dataType === 'question' && 
          item.questionCourseDate === selectedDayDataID
        )
        .sort((a, b) => parseInt(b.dataIndex) - parseInt(a.dataIndex));

      let nextDataIndex = 1;
      if (existingQuestionsForDay.length > 0) {
        nextDataIndex = parseInt(existingQuestionsForDay[0].dataIndex) + 1;
      }

      // Import each selected question
      // First pass: Create mapping of old dataIDs to new dataIDs
      const idMapping = {};
      const questionsWithNewIds = [];
      
      for (const question of questionsToImport) {
        const newDataID = generateUniqueId();
        idMapping[question.dataID] = newDataID; // Map original dataID to new dataID
        
        questionsWithNewIds.push({
          ...question,
          newDataID,
          newDataIndex: nextDataIndex.toString()
        });
        
        nextDataIndex++;
      }

      // Second pass: Update questionLogic to use new IDs and import questions
      for (const question of questionsWithNewIds) {
        let updatedQuestionLogic = question.questionLogic || '';
        
        // Update any references to other questions being imported in this batch
        // questionLogic uses format: (questionID, answerNumber)
        for (const [oldId, newId] of Object.entries(idMapping)) {
          // Replace (oldId, number) with (newId, number) - preserving the answer number
          const oldIdPattern = new RegExp(`\\(${oldId},`, 'g');
          updatedQuestionLogic = updatedQuestionLogic.replace(oldIdPattern, `(${newId},`);
        }
        
        // Create new question entry
        const newQuestionData = {
          dataType: 'question',
          dataID: question.newDataID,
          dataText: question.dataText,
          dataIndex: question.newDataIndex,
          questionType: question.questionType,
          questionOptions: question.questionOptions || '',
          questionLogic: updatedQuestionLogic,
          questionCourseDate: selectedDayDataID
        };

        // Add to course design state via handleChange
        await handleChange(
          'question',
          newQuestionData.dataID,
          newQuestionData.dataText,
          {
            dataIndex: newQuestionData.dataIndex,
            questionType: newQuestionData.questionType,
            questionOptions: newQuestionData.questionOptions,
            questionLogic: newQuestionData.questionLogic,
            questionCourseDate: newQuestionData.questionCourseDate
          }
        );
      }

      // Show success message
      setMessageContent({
        title: 'Import Successful',
        message: `Successfully imported ${questionsToImport.length} question${questionsToImport.length !== 1 ? 's' : ''} to the selected day.`,
        type: 'success'
      });
      setShouldNavigateBackOnClose(true); // Set flag to navigate back on close
      setShowMessage(true);

      // Clear selection
      setSelectedQuestions(new Set());

    } catch (error) {
      console.error('Error importing questions:', error);
      setMessageContent({
        title: 'Import Failed',
        message: 'An error occurred while importing the questions. Please try again.',
        type: 'error'
      });
      setShowMessage(true);
    } finally {
      setIsImporting(false);
    }
  };

  const formatSharedDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div style={{
      padding: '20px',
      fontFamily: 'var(--font-primary)',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: 'var(--standard-box-shadow)'
    }}>
      {/* Header with back button and module info */}
      <div style={{
        marginBottom: '24px'
      }}>
        <button
          onClick={onBack}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--color2)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '16px'
          }}
        >
          ← Back to Modules
        </button>
        
        <div style={{ textAlign: 'center' }}>
          <h2 style={{
            margin: '0 0 8px 0',
            fontSize: '24px',
            fontWeight: '600',
            color: 'var(--color1)'
          }}>
            {module.questionModule}
          </h2>
          <p style={{
            margin: '0 0 8px 0',
            color: 'var(--text-color-muted)',
            fontSize: '14px'
          }}>
            {module.className} • Shared by {module.sharerName}
          </p>
          <p style={{
            margin: 0,
            color: 'var(--text-color-muted)',
            fontSize: '12px'
          }}>
            Shared on {formatSharedDate(module.sharedAt)}
          </p>
        </div>
      </div>

      {/* Three-way segmented control for view mode */}
      <div style={{
        display: 'flex',
        backgroundColor: '#f0f0f0',
        borderRadius: '8px',
        padding: '4px',
        marginBottom: '20px',
        width: 'fit-content',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        gap: '4px'
      }}>
        {[
          { key: 'import', label: 'Import', icon: '�️' },
          { key: 'student', label: 'Student', icon: '�' },
          { key: 'logic', label: 'Logic', icon: '�' }
        ].map((option) => (
          <button
            key={option.key}
            onClick={() => handleViewModeChange(option.key)}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: viewMode === option.key ? 'var(--color1)' : 'var(--color2light)',
              color: 'white',
              fontWeight: viewMode === option.key ? '600' : '400',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
              minWidth: '120px',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              if (viewMode !== option.key) {
                e.currentTarget.style.backgroundColor = 'var(--color2verylight)';
              }
            }}
            onMouseLeave={(e) => {
              if (viewMode !== option.key) {
                e.currentTarget.style.backgroundColor = 'var(--color2light)';
              }
            }}
          >
            <span>{option.icon}</span>
            <span>{option.label}</span>
          </button>
        ))}
      </div>

      {/* Content based on view mode */}
      {viewMode === 'import' && (
        <QuestionImportQuestionList
          questions={transformForImportList()}
          selectedQuestions={selectedQuestions}
          onQuestionToggle={handleQuestionToggle}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          onImport={handleImport}
          isImporting={isImporting}
        />
      )}

      {viewMode === 'student' && (
        <div style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          padding: '20px',
          border: '1px solid #e9ecef'
        }}>
          {/* Use QAndA component with mock context for consistency */}
          <CourseDesignContext.Provider value={mockContextValue}>
            <StudentAnswerContext.Provider value={mockStudentAnswerContext}>
              <QAndA
                username="preview-user"
                sectionsToShow={['shared-section']}
                answersReadOnly={false}
                showSectionHeaders={false}
                showMyPhiHeader={false}
                ignoreCurrentCourseDate={true}
              />
            </StudentAnswerContext.Provider>
          </CourseDesignContext.Provider>
          
          {module.questions.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#666'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>📝</div>
              <h3 style={{ marginBottom: '10px', color: '#495057' }}>No Questions</h3>
              <p>This module doesn't have any questions to preview.</p>
            </div>
          )}
        </div>
      )}

      {viewMode === 'logic' && (
        <div>
          {module.questions.length > 0 ? (
            <>
              {/* Check if any questions have logic defined */}
              {module.questions.some(q => q.questionLogic && q.questionLogic.trim() !== '') ? (
                <div style={{ height: '600px', width: '100%' }}>
                  <QuestionLogicRepresentation 
                    courseDesignState={transformToCourseDesignState()}
                    filterByDate="shared-module" // Use our consistent date
                    height="600px"
                    direction="TB"
                    showComplexityIndicators={true}
                    enableMiniMap={true}
                    onQuestionSelect={(question) => {
                      console.log('Selected shared question:', question);
                    }}
                  />
                </div>
              ) : (
                <div style={{
                  padding: '60px 20px',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '2px dashed #dee2e6'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔗</div>
                  <h3 style={{ marginBottom: '10px', color: '#495057' }}>No Logic Dependencies</h3>
                  <p style={{ color: '#6c757d', marginBottom: '20px' }}>
                    The questions in this shared module don't have logic dependencies defined.
                  </p>
                  <div style={{
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    textAlign: 'left'
                  }}>
                    <h4 style={{ marginBottom: '12px', color: 'var(--color1)' }}>Questions in this module:</h4>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {module.questions.map((question, index) => (
                        <li key={question.id} style={{ marginBottom: '8px', lineHeight: '1.4' }}>
                          <strong>Question {index + 1}:</strong> {question.dataText?.substring(0, 80)}
                          {question.dataText?.length > 80 ? '...' : ''}
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#6c757d', 
                            marginTop: '4px',
                            fontFamily: 'monospace',
                            backgroundColor: '#f8f9fa',
                            padding: '4px 8px',
                            borderRadius: '4px'
                          }}>
                            Type: {question.questionType || 'Unknown'} • Logic: {question.questionLogic || 'None'}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px',
              color: '#666',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '2px dashed #dee2e6'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔗</div>
              <h3 style={{ marginBottom: '10px', color: '#495057' }}>No Questions</h3>
              <p>This module doesn't have any questions to show logic relationships for</p>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <DaySelectionModal
        isOpen={showDaySelectionModal}
        onClose={() => setShowDaySelectionModal(false)}
        onSelectDay={handleDaySelection}
        selectedQuestions={Array.from(selectedQuestions)}
      />

      {showMessage && (
        <MessageModal
          isOpen={showMessage}
          onClose={() => {
            setShowMessage(false);
            if (shouldNavigateBackOnClose) {
              setShouldNavigateBackOnClose(false);
              onBack(); // Navigate back to question modules list
            }
          }}
          title={messageContent.title}
          message={messageContent.message}
          type={messageContent.type}
        />
      )}
    </div>
  );
};

export default SharedQuestionModule;
